import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {parseServerDate, parseTaxRate} from "./posMath";

// Caisse web — mêmes endpoints que la caisse mobile (pos_clone
// services/DataSyncService.js et BackgroundSyncService.js) :
//   GET  /getCatalogue           catalogue complet (produits, remises, clients…)
//   POST /journal/sync           ouverture / clôture de la session de caisse
//   POST /sale/sync              la vente (upsert par id)
//   POST /sale_items/sync        une ligne
//   POST /sale_discounts/sync    une remise de ligne
//   POST /sale_payments/sync     un paiement
// Les /…/sync attendent du multipart (FormData), un id UUID généré ici.

// Nom de caisse du journal web : permet de retrouver la session ouverte après
// un rechargement, et de la distinguer des tablettes dans Activité > Journaux.
export const WEB_REGISTER_NAME = "Caisse Web";
export const SALE_STATUS_DONE = "terminer";
// Ligne « vente libre » (article hors catalogue) : même product_id réservé
// que la caisse desktop (electron-pos salesSlice addFreeSaleItem), le libellé
// voyage dans la note de la ligne.
export const FREE_SALE_ITEM = "FREE_SALE_ITEM";
// Mouvement de fonds (entrée / sortie d'espèces) : une ligne `sales` avec ce
// statut, total signé (+ entrée, − sortie), comme electron-pos
// fundsMovementService.createMovement.
export const FUNDS_MOVEMENT_STATUS = "mouvement_fonds";

const accountHeaders = async (extra = {}) => {
    const account = await secureStorage.getItem("selectedAccount");
    return {"X-account-id": account?.id, Accept: "application/json", ...extra};
};

export const apiErrorMessage = (error, fallback) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    fallback;

export const newId = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });

const toFormData = (fields) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
        if (value !== null && value !== undefined) fd.append(key, String(value));
    });
    return fd;
};

const postSync = async (endpoint, fields) => {
    const headers = await accountHeaders({"Content-Type": "multipart/form-data"});
    return api.post(endpoint, toFormData(fields), {headers});
};

// ── Catalogue ───────────────────────────────────────────────────────────────

const idOf = (...candidates) => candidates.find((c) => c) || null;

const productTaxId = (p) =>
    idOf(p?.taxes?.id, p?.taxesId, p?.taxe?.id, p?.taxeId, p?.tax?.id);

// Réponse brute de /getCatalogue → structures prêtes pour l'écran.
export const normalizeCatalogue = (data = {}) => {
    const taxRateById = new Map((data.taxes || []).map((t) => [t.id, parseTaxRate(t.value)]));
    const rateFor = (taxId) => (taxId ? taxRateById.get(taxId) || 0 : 0);

    const categories = (data.categories || [])
        .filter((c) => c.id && c.active !== false)
        .sort((a, b) => (a.pos_order || 0) - (b.pos_order || 0) || String(a.name).localeCompare(String(b.name)))
        .map((c) => ({id: c.id, name: c.name, color: c.color || null, image: c.image || null}));

    const seen = new Set();
    const products = [];
    (data.products || []).forEach((p) => {
        if (!p.id || seen.has(p.id) || p.active === false || p.isVendable === false) return;
        seen.add(p.id);
        const taxId = productTaxId(p);
        const variants = (p.variants || [])
            .filter((v) => v.id && v.active !== false && v.isVendable !== false)
            .map((v) => {
                const vTax = productTaxId(v) || taxId;
                return {
                    id: v.id,
                    name: v.name,
                    price: Number(v.price) || 0,
                    barcode: v.barcode || "",
                    taxRate: rateFor(vTax),
                    isStockable: v.isStockable,
                    isVendable: v.isVendable,
                };
            });
        products.push({
            id: p.id,
            name: p.name,
            price: Number(p.price) || 0,
            barcode: p.barcode || "",
            color: p.color || null,
            image: p.image || null,
            categoryId: idOf(p.category?.id, p.categoryId),
            taxRate: rateFor(taxId),
            isStockable: p.isStockable,
            isVendable: p.isVendable,
            variants,
        });
    });
    products.sort((a, b) => String(a.name).localeCompare(String(b.name)));

    const payments = (data.payments || [])
        .filter((p) => p.id && p.active !== false)
        .map((p) => ({id: p.id, name: p.name, icon: p.icon}));

    const discounts = (data.discounts || []).filter((d) => d.id && d.active !== false);

    const clients = (data.clients || [])
        .filter((c) => c.id && c.active !== false)
        .map((c) => ({
            id: c.id,
            name: [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || c.phone || "Sans nom",
            phone: c.phone || "",
            email: c.email || "",
            clientTypeId: c.clientType?.id || null,
            clientTypeName: c.clientType?.name || null,
        }));

    const employees = (data.employees || [])
        .filter((e) => e.id && e.active !== false)
        .map((e) => ({id: e.id, name: e.name || e.email || "Employé", email: e.email || ""}));

    const journals = (data.journals || []).map((j) => ({
        id: j.id,
        startDate: parseServerDate(j.startDate),
        endDate: parseServerDate(j.endDate),
        startTotal: Number(j.startTotal) || 0,
        startEmployeeId: j.startEmployee?.id || null,
        registerName: j.registerName || null,
        isOpen: !j.endDate,
    }));

    return {categories, products, payments, discounts, clients, employees, journals};
};

export const fetchCatalogue = async () => {
    const headers = await accountHeaders({"X-last-request": "2020-01-01T00:00:00+00:00"});
    const {data} = await api.get("/getCatalogue", {headers, timeout: 60000});
    return normalizeCatalogue(data);
};

// ── Journal (session de caisse) ─────────────────────────────────────────────

const journalFields = (j) => ({
    id: j.id,
    start_employee_id: `/api/employee/${j.startEmployeeId}`,
    end_employee_id: j.endEmployeeId ? `/api/employee/${j.endEmployeeId}` : null,
    is_open: String(!!j.isOpen),
    start_date: j.startDate,
    end_date: j.endDate || "",
    created_at: j.startDate,
    updated_at: Date.now(),
    start_total: String(Number(j.startTotal) || 0),
    end_total: String(Number(j.endTotal) || 0),
    register_name: WEB_REGISTER_NAME,
    active: "true",
});

export const openJournal = async ({employeeId, startTotal}) => {
    const journal = {
        id: newId(),
        startEmployeeId: employeeId,
        startDate: Date.now(),
        startTotal: Number(startTotal) || 0,
        isOpen: true,
        registerName: WEB_REGISTER_NAME,
    };
    await postSync("/journal/sync", journalFields(journal));
    return journal;
};

export const closeJournal = async ({journal, employeeId, endTotal}) => {
    await postSync("/journal/sync", journalFields({
        ...journal,
        isOpen: false,
        endEmployeeId: employeeId,
        endDate: Date.now(),
        endTotal: Number(endTotal) || 0,
    }));
};

// Espèces attendues en caisse : détail du journal (même endpoint que
// Activité > Journaux). Renvoie la réponse brute, ou null si indisponible.
export const fetchJournalDetails = async (journalId) => {
    try {
        const {data} = await api.get(`/journal/details/${journalId}`, {headers: await accountHeaders()});
        return data;
    } catch {
        return null;
    }
};

// ── Vente ───────────────────────────────────────────────────────────────────

// sale : {id, total, totalHt, totalRemise, note, clientId, employeeId, journalId}
// lines : [{id, productId, price, quantity, taxRate, note,
//           discounts: [{lineDiscountId, id, value, type}]}]
// payments : [{id, paymentId, amount}]
// Tous les ids sont fixés à la création (pas à l'envoi) : un nouvel essai
// après une coupure ré-upserte les mêmes lignes au lieu de les dupliquer.
// Envoi dans l'ordre vente → lignes → remises/paiements : les lignes
// référencent la vente, les remises référencent leur ligne.
export const submitSale = async ({sale, lines, payments}) => {
    const now = Date.now();
    await postSync("/sale/sync", {
        id: sale.id,
        status: SALE_STATUS_DONE,
        total: sale.total,
        total_ht: sale.totalHt,
        total_remise: sale.totalRemise,
        note: sale.note || null,
        active: "0",
        created_at: now,
        updated_at: now,
        employee_id_created: sale.employeeId,
        employee_id_updated: sale.employeeId,
        journal_id: sale.journalId,
        client_id: sale.clientId || null,
        device_name_creation: WEB_REGISTER_NAME,
        device_name_updated: WEB_REGISTER_NAME,
        is_refund: "0",
    });

    for (const line of lines) {
        // eslint-disable-next-line no-await-in-loop
        await postSync("/sale_items/sync", {
            id: line.id,
            sale_id: sale.id,
            product_id: line.isFree ? FREE_SALE_ITEM : line.productId,
            employee_id: sale.employeeId,
            price: line.price,
            quantity: line.quantity,
            taxe_value: line.taxRate ? Math.round(line.taxRate * 10000) / 100 : null,
            note: (line.isFree ? line.name : line.note) || null,
            created_at: now,
            kitchen_printed: "0",
        });
    }

    const children = [];
    lines.forEach((line) => (line.discounts || []).forEach((d) => {
        children.push(postSync("/sale_discounts/sync", {
            id: d.lineDiscountId,
            sale_items_id: line.id,
            discount_id: d.id,
            amount: d.value,
            type: d.type || "%",
            employee_id: sale.employeeId,
            created_at: now,
        }));
    }));
    payments.forEach((p) => children.push(postSync("/sale_payments/sync", {
        id: p.id,
        sale_id: sale.id,
        payment_id: p.paymentId,
        amount: p.amount,
        created_at: now,
    })));
    await Promise.all(children);
    return sale.id;
};

// ── Mouvement de fonds ──────────────────────────────────────────────────────

export const FUNDS_CATEGORIES = {
    sortie: [
        {value: "fournitures", label: "Fournitures"},
        {value: "eau", label: "Eau"},
        {value: "cafe_the", label: "Café / Thé"},
        {value: "livreur", label: "Livreur"},
        {value: "remboursement", label: "Remboursement"},
        {value: "petites_depenses", label: "Petites dépenses"},
        {value: "maintenance", label: "Maintenance"},
        {value: "autre", label: "Autre"},
    ],
    entree: [
        {value: "apport_caisse", label: "Apport caisse"},
        {value: "correction", label: "Correction"},
        {value: "remboursement_fournisseur", label: "Remboursement fournisseur"},
        {value: "autre", label: "Autre"},
    ],
};
export const FUNDS_MAX_AMOUNT = 10000;

// Champs /sale/sync d'un mouvement — même contrat que electron-pos
// pushSyncService (movementFieldsFor) : note JSON + colonnes movement_*.
export const fundsMovementFields = ({id, type, amount, motif, category, employeeId, journalId, now = Date.now()}) => {
    const value = Math.abs(Number(amount) || 0);
    return {
        id,
        status: FUNDS_MOVEMENT_STATUS,
        total: type === "sortie" ? -value : value,
        total_ht: 0,
        total_remise: 0,
        note: JSON.stringify({type, motif, category: category || "", isFundsMovement: true, timestamp: now}),
        movement_type: type,
        movement_category: category || "",
        movement_cancelled: "0",
        active: "0",
        created_at: now,
        updated_at: now,
        employee_id_created: employeeId,
        employee_id_updated: employeeId,
        journal_id: journalId,
        device_name_creation: WEB_REGISTER_NAME,
        device_name_updated: WEB_REGISTER_NAME,
        is_refund: "0",
    };
};

export const submitFundsMovement = async (movement) => {
    const fields = fundsMovementFields(movement);
    await postSync("/sale/sync", fields);
    return {id: fields.id, total: fields.total};
};

// ── Ventes en attente (partagées entre caisses) ─────────────────────────────
// Même contrat que la caisse desktop (electron-pos services/heldSaleSync.js et
// salesSlice fetchPendingSalesFromAPI / deletePendingSale) :
//   POST   /sales_real/pending   {sale, saleItems, salePayments, client} (JSON)
//   GET    /sales_real/pending   {pending_sales: [{sale, saleItems, client…}]}
//   DELETE /sales_real/{id}      404 = déjà supprimée
// L'id de la vente est généré ici et adopté tel quel par le serveur : encaisser
// le ticket repris (/sale/sync, même id) le fait passer de « en attente » à
// « terminer ».

export const SALE_STATUS_PENDING = "en_attente";

export const heldSalePayload = ({sale, label, totals, employeeId, journalId, now = Date.now()}) => ({
    sale: {
        id: sale.id,
        status: SALE_STATUS_PENDING,
        name: label || null,
        total: totals.total,
        total_ht: totals.ht,
        total_remise: totals.discount,
        charge: 0,
        note: sale.note || null,
        employee_id_created: employeeId || null,
        client_id: sale.client?.id || null,
        journal_id: journalId || "",
        device_name_creation: WEB_REGISTER_NAME,
        active: false,
        created_at: sale.createdAt || now,
    },
    saleItems: sale.lines.map((l) => ({
        id: l.id,
        product_id: l.isFree ? FREE_SALE_ITEM : l.productId,
        employee_id: employeeId || null,
        quantity: l.quantity,
        price: l.price,
        taxe_value: l.taxRate ? Math.round(l.taxRate * 10000) / 100 : 0,
        note: (l.isFree ? l.name : l.note) || null,
        discount_id: l.discounts?.[0]?.id || null,
    })),
    salePayments: [],
    client: sale.client ? {id: sale.client.id} : null,
});

export const pushHeldSale = async (args) => {
    const headers = await accountHeaders({"Content-Type": "application/json"});
    await api.post("/sales_real/pending", heldSalePayload(args), {headers});
};

export const fetchPendingSales = async () => {
    const {data} = await api.get("/sales_real/pending", {headers: await accountHeaders()});
    if (Array.isArray(data)) return data;
    return data?.pending_sales || data?.data || [];
};

export const deletePendingSale = async (saleId) => {
    try {
        await api.delete(`/sales_real/${saleId}`, {headers: await accountHeaders()});
    } catch (e) {
        if (e?.response?.status !== 404) throw e;
    }
};

// Lignes retirées d'un ticket repris : à supprimer côté serveur, sinon elles
// resteraient rattachées à la vente. Au mieux : une erreur n'arrête rien.
export const deleteSaleItems = async (ids) => {
    if (!ids?.length) return;
    const headers = await accountHeaders();
    await Promise.all(ids.map((id) => api.delete(`/sale_items/${id}`, {headers}).catch(() => null)));
};

const uuidOf = (obj, flat) => obj?.id || obj?._id || flat || null;

const toMs = (value) => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number" || /^\d+$/.test(String(value))) {
        const n = Number(value);
        return n < 1e12 ? n * 1000 : n;
    }
    return parseServerDate(value);
};

// Ligne serveur → ticket en attente affichable et reprenable dans le panier.
// productIndex : Map id → {product, variant} ; clientsById, discountsById : Map.
export const normalizePendingSale = (row, {productIndex, clientsById, discountsById, categoriesById}) => {
    const s = row?.sale || row || {};
    const id = s.id || s._id;
    if (!id) return null;
    const items = row.saleItems || s.saleItems || s.sale_items || s.items || [];
    const clientObj = row.client || s.client;
    const clientId = uuidOf(clientObj, s.client_id);
    const known = clientId ? clientsById.get(clientId) : null;
    const client = known || (clientId ? {
        id: clientId,
        name: [clientObj?.first_name || clientObj?.firstName, clientObj?.last_name || clientObj?.lastName]
            .filter(Boolean).join(" ").trim() || clientObj?.phone || "Client",
        phone: clientObj?.phone || "",
        email: clientObj?.email || "",
        clientTypeId: null,
        clientTypeName: null,
    } : null);
    const employeeObj = row.employee_created || row.employee || s.employee_created;

    const lines = items.map((it) => {
        const productId = uuidOf(it.product || it.variant, it.product_id || it.variant_id);
        const isFree = productId === FREE_SALE_ITEM;
        const found = !isFree && productId ? productIndex.get(productId) : null;
        const item = found ? (found.variant || found.product) : null;
        const product = found?.product;
        const category = product ? categoriesById.get(product.categoryId) : null;
        const discount = discountsById.get(uuidOf(it.discount, it.discount_id));
        const name = isFree
            ? (it.note || "Vente libre")
            : found
                ? (found.variant ? `${product.name} — ${found.variant.name}` : product.name)
                : (it.product?.name || "Article");
        const price = Number(it.price) || 0;
        return {
            id: it.id || it._id || newId(),
            productId: isFree ? null : productId,
            isFree,
            name,
            price,
            basePrice: item ? item.price : price,
            quantity: Number(it.quantity) || 1,
            taxRate: item ? item.taxRate : parseTaxRate(it.taxe_value),
            note: isFree ? "" : (it.note || ""),
            product: item ? {isStockable: item.isStockable, isVendable: item.isVendable} : null,
            image: product?.image || category?.image || null,
            color: isFree ? "#8B5CF6" : (product?.color || category?.color || null),
            discounts: discount ? [{
                lineDiscountId: newId(), id: discount.id, name: discount.name,
                value: Number(discount.value) || 0, type: discount.type,
            }] : [],
        };
    });

    return {
        id,
        label: s.name || null,
        createdAt: toMs(s.created_at) || Date.now(),
        total: Number(s.total) || 0,
        employeeId: uuidOf(employeeObj, s.employee_id_created),
        device: s.device_name_creation || null,
        remote: true,
        sale: {id, lines, client, note: s.note || "", serverItemIds: lines.map((l) => l.id)},
    };
};

// Facture PDF (même endpoint que la fiche client) → URL à ouvrir.
export const generateInvoice = async (saleId) => {
    const {data} = await api.get(`/generate_invoice/${saleId}`, {headers: await accountHeaders()});
    if (data?.status === "OK" && data?.url) return data;
    throw new Error("Erreur lors de la génération de la facture");
};
